import { useEffect, useRef, useState } from "react";
import { Crown, Trophy } from "lucide-react";
import { useTranslation } from "../../../i18n";
import { crossedMilestone, type MilestoneTier } from "../milestones";
import {
    TROPHY_BADGE_EVENT,
    TROPHY_OPEN_EVENT,
    getTrophyBadge,
    trophyOpenEligibleInMs,
    type TrophyBadge,
    type TrophyFlow,
} from "../trophies";
import { trophyGateConfig } from "../pointsSettings";
import "../../../styles/gamification.css";
import "../../../styles/trophies.css";

interface ScoreDisplayProps {
    total: number;
    /** Names the department for the trophy modal's opening view. */
    flow?: TrophyFlow;
}

const TWEEN_MS = 600;

// How long the pill itself celebrates. The ladder's own durations are longer
// for the big tiers, but those are carried by the full-screen overlay — a pill
// still glowing three seconds after the fact just looks stuck.
const PILL_CELEBRATE_MS: Record<MilestoneTier, number> = { 1: 1700, 2: 2100, 3: 2100 };

// A bigger milestone throws more paper.
const CONFETTI_PIECES: Record<MilestoneTier, number> = { 1: 12, 2: 24, 3: 24 };

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const formatScore = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(2)}k` : `${n}`);

const ScoreDisplay = ({ total, flow }: ScoreDisplayProps) => {
    const { t } = useTranslation();
    const [displayed, setDisplayed] = useState(total);
    const [pop, setPop] = useState(false);
    const [celebrate, setCelebrate] = useState<MilestoneTier | null>(null);
    const [badge, setBadge] = useState<TrophyBadge>(() => getTrophyBadge());
    // The trophy gate: the modal opens manually only once per interval. The
    // pill wears a glow while an open is available and shakes off a tap while
    // it isn't — the state must be readable at a glance.
    const [trophyReady, setTrophyReady] = useState(false);
    const [shake, setShake] = useState(false);
    const rafRef = useRef<number | null>(null);
    const fromRef = useRef(total);
    const startRef = useRef(0);
    const prevTotalRef = useRef(total);

    useEffect(() => {
        if (total === prevTotalRef.current) return;

        const prev = prevTotalRef.current;
        prevTotalRef.current = total;

        if (total > prev) {
            setPop(true);
            const popT = setTimeout(() => setPop(false), 420);

            // The ladder decides — 250 and 500 are a burst, 1,000 and 2,500 a
            // bigger one, and 5,000+ hands the moment to MilestoneCelebration
            // while the pill still marks the number that changed.
            const crossed = crossedMilestone(prev, total);
            if (crossed) {
                setCelebrate(crossed.tier);
                setTimeout(() => setCelebrate(null), PILL_CELEBRATE_MS[crossed.tier]);
            }

            fromRef.current = displayed;
            startRef.current = performance.now();
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            const tick = () => {
                const elapsed = performance.now() - startRef.current;
                const ratio = Math.min(1, elapsed / TWEEN_MS);
                const eased = easeOutCubic(ratio);
                const value = Math.round(fromRef.current + (total - fromRef.current) * eased);
                setDisplayed(value);
                if (ratio < 1) rafRef.current = requestAnimationFrame(tick);
                else rafRef.current = null;
            };
            rafRef.current = requestAnimationFrame(tick);

            return () => clearTimeout(popT);
        }

        setDisplayed(total);
    }, [total, displayed]);

    useEffect(() => () => {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    }, []);

    useEffect(() => {
        const handler = (e: Event) => {
            const detail = (e as CustomEvent<TrophyBadge>).detail;
            setBadge(detail ?? getTrophyBadge());
        };
        window.addEventListener(TROPHY_BADGE_EVENT, handler as EventListener);
        return () => window.removeEventListener(TROPHY_BADGE_EVENT, handler as EventListener);
    }, []);

    useEffect(() => {
        const check = () =>
            setTrophyReady(trophyOpenEligibleInMs(trophyGateConfig().intervalMinutes) === 0);
        check();
        const timer = setInterval(check, 15_000);
        return () => clearInterval(timer);
    }, []);

    const openTrophies = () => {
        if (trophyOpenEligibleInMs(trophyGateConfig().intervalMinutes) > 0) {
            // Not yet — a brief shake says "heard you, not now" without a toast.
            setShake(true);
            setTimeout(() => setShake(false), 400);
            return;
        }
        setTrophyReady(false);
        try {
            window.dispatchEvent(new CustomEvent(TROPHY_OPEN_EVENT, { detail: { flow } }));
        } catch {
            // noop
        }
    };

    return (
        <div
            className={
                "gamif-score-pill gamif-score-pill--tappable"
                + (trophyReady ? " gamif-score-pill--trophy-ready" : "")
                + (shake ? " gamif-score-pill--shake" : "")
                + (pop ? " gamif-score-pop" : "")
                + (celebrate ? " gamif-score-celebrate" : "")
                + (celebrate && celebrate >= 2 ? " gamif-score-celebrate-big" : "")
            }
            aria-label={t("score_label")}
            role="button"
            onClick={openTrophies}
        >
            <Trophy size={16} className="gamif-score-icon" />
            <span className="gamif-score-value">{formatScore(displayed)}</span>
            {badge.recordsHeld > 0 && (
                <span className="trophy-pill-crown" data-testid="trophy-pill-crown">
                    <Crown size={12} />
                </span>
            )}
            {badge.unseen > 0 && <span className="trophy-pill-dot" data-testid="trophy-pill-dot" />}
            {celebrate && (
                <>
                    {Array.from({ length: CONFETTI_PIECES[celebrate] }, (_, i) => (
                        <span
                            key={i}
                            className={
                                `gamif-confetti-piece gamif-confetti-piece-${(i % 12) + 1}`
                                + (i >= 12 ? " gamif-confetti-piece-second" : "")
                            }
                        />
                    ))}
                </>
            )}
        </div>
    );
};

export default ScoreDisplay;
